import {
  Dispatch,
  HTMLAttributes,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslate } from '@tolgee/react';
import { Spu, Knowledge } from './type';
import { useRequest } from 'ahooks';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';
import { countSku, splitItems } from '@knowledge/common/helpers/sku';
import { css, cx } from '@emotion/css';
import { useStoreInfo } from '@knowledge/common/components/ItemSelectV2/useStoreInfo';
import {
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  LinkOutlined,
  UpOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import SafeImage from 'components/SafeImage';
import { collapseOperationState } from './CollapseOperation';
import { findIndex, cloneDeep } from 'lodash-es';
import { useAtomValue } from 'jotai';

interface ItemRenderProps extends HTMLAttributes<HTMLDivElement> {
  item: ItemSelectV2.Item;
  handleRemove: (spu: Spu) => void;
}
const ItemRender = memo((props: ItemRenderProps) => {
  const { item, className, handleRemove, ...rest } = props;
  const { t } = useTranslate();
  const {
    itemName,
    imageUrl: [imageUrl] = [],
    externalItemId: spuId,
    internalItemId,
    skuInfo = [],
    itemUrl,
    itemSku,
  } = item;
  const storeStr = useStoreInfo(item);
  const foldState = useAtomValue(collapseOperationState);
  const [open, setOpen] = useState<'fold' | 'unfold'>('fold');
  useEffect(() => {
    setOpen(foldState);
  }, [foldState]);

  return (
    <div className={className} {...rest}>
      <div className="flex items-start">
        <div className="basis-auto overflow-hidden flex flex-grow">
          <div className="flex-shrink-0 !mr-[8px]">
            <Image hasSmallPic="both" src={imageUrl} width={60} height={60} />
          </div>
          <div className="basis-auto flex-grow overflow-hidden flex flex-col">
            <div className="h-[20px] flex items-start">
              <div
                title={itemName}
                className="w-full group line-clamp-1 break-all cursor-pointer leading-[18px] hover:text-[#3366ff]"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(itemUrl, '_blank');
                }}
              >
                <LinkOutlined className="!hidden float-right bg-[#d6e8ff] !text-[#336fff] h-[20px] w-[20px] !leading-[20px] group-hover:!block" />
                {itemName}
              </div>
              <div>
                <DeleteOutlined
                  onClick={() => {
                    handleRemove({
                      itemInternalId: internalItemId,
                      skus: skuInfo,
                    });
                  }}
                  className="cursor-pointer flex-shrink-0 ml-[4px]"
                />
              </div>
            </div>
            <div className="mt-[4px] leading-[18px] text-[12px] text-[#84868c] flex-grow flex flex-col justify-between">
              <div className="truncate" title={storeStr}>
                {t('marketing.店铺')}: {storeStr}
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center truncate basis-auto overflow-hidden mr-[8px]">
                  <span>ID: {spuId}</span>
                  <div className="mx-[4px]">|</div>
                  <span className="truncate">
                    {t('aide.主商品货号')}: {itemSku || '-'}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center justify-end">
                <div
                  onClick={() => {
                    setOpen(open === 'fold' ? 'unfold' : 'fold');
                  }}
                  className="cursor-pointer hover:text-[#3366ff]"
                >
                  {t('knowledge.共{{x}}个SKU', { x: skuInfo?.length })}
                  {open === 'fold' ? (
                    <DownOutlined className="ml-[4px]" />
                  ) : (
                    <UpOutlined className="ml-[4px]" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {open === 'unfold' && (
        <div className="mt-[8px]">
          <div className="border-0 border-t border-solid border-[#f0f2f5] overflow-hidden">
            {skuInfo.map((sku) => {
              return (
                <div
                  key={sku.skuId}
                  className="flex-shrink-0 flex-grow-0 mt-[9px] pr-[8px] overflow-hidden flex justify-between items-center"
                >
                  <div
                    title={`${sku.sellerSku}(${sku.skuName})`}
                    className="w-full truncate break-all"
                  >
                    {sku.sellerSku}({sku.skuName})
                  </div>
                  <CloseCircleOutlined
                    onClick={() => {
                      handleRemove({
                        itemInternalId: internalItemId,
                        skus: [sku],
                      });
                    }}
                    className="flex-shrink-0 cursor-pointer"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

ItemRender.displayName = 'ConfigedSkuModalItemRender';

const Image = styled(SafeImage)`
  width: 68px;
  height: 68px;
`;

interface ConfigedSkuButtonProps {
  active: boolean;
  selectedSpus: Spu[];
  selectedKnowledgeId: string;
  selectedKnowledgeValueId: string;
  setKnowledges: Dispatch<SetStateAction<Knowledge[]>>;
}
export const ConfigedSkuButton = (props: ConfigedSkuButtonProps) => {
  const { selectedSpus, active, selectedKnowledgeValueId, selectedKnowledgeId, setKnowledges } =
    props;
  const { t } = useTranslate();
  const count = countSku(selectedSpus || [], 'skus');
  const [visible, setVisible] = useState(false);
  const [items, setItems] = useState<ItemSelectV2.Item[]>([]);
  useRequest(() => getItemsByInternalIds(selectedSpus), {
    refreshDeps: [selectedSpus],
    ready: !!selectedSpus,
    onSuccess: (data) => {
      setItems(data);
    },
  });
  const handleRemove = useCallback(
    (spu: Spu) => {
      setKnowledges((prevState) => {
        const ks = cloneDeep(prevState || []);
        const index = findIndex(ks, (k) => k.id === selectedKnowledgeId);
        const newk = ks[index];
        newk.values = newk.values.map((v) => {
          if (v.id === selectedKnowledgeValueId) {
            return {
              ...v,
              spus: splitItems(v.spus!, [spu]),
            };
          }
          return v;
        });
        ks.splice(index, 1, newk);
        return ks;
      });
    },
    [selectedKnowledgeId, selectedKnowledgeValueId, setKnowledges],
  );
  useEffect(() => {
    setVisible(active);
  }, [active]);
  return (
    <div>
      <div
        className={cx(
          css`
            box-shadow: inset 0 1px 0 0 #f0f2f5;
          `,
          'py-[10px] pr-[16px] flex justify-end items-center',
        )}
      >
        <div
          onClick={() => {
            setVisible(!visible);
          }}
          className="text-[#515359] cursor-pointer flex items-center"
        >
          {visible && <UpOutlined style={{ fontSize: 10, marginRight: 4 }} />}
          {t('config_yet', { x: count })}
        </div>
      </div>

      {visible && !!items.length && (
        <div className="bg-[#f7f9fa] px-[16px] py-[12px]">
          {items.map((each) => {
            return (
              <ItemRender
                className={css`
                  background: #fff;
                  box-sizing: border-box;
                  border: 1px solid rgba(211, 215, 224, 1);
                  border-radius: 2px;
                  padding: 10px 13px 12px 8px;
                  margin-bottom: 16px;
                  &:last-child {
                    margin-bottom: 0;
                  }
                `}
                key={each.internalItemId}
                item={each}
                handleRemove={handleRemove}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
